import React, { useState, useEffect } from "react";
import { TriggerStepCreate } from "../../../../../libs/types/src";
import { useForm, Controller } from "react-hook-form";
import produce from "immer";
import { saveDraft, updateStep } from "./reducer/actionCreators";
import { useTriggerEditorStateDispatch } from "./TriggerEditor";
import invariant from "ts-invariant";
import { StepFormWithCommonParts } from "./TriggerStepForm";
import { FormLabel } from "./FormLabel";
import { Input } from "./Input";
import TriggerEditorChannelSelector from "./TriggerEditorChannelSelector";
/**
 * Renders the form for a "Delete channel" step. When the form is submitted, generate the step data and publish an update action to add a new step to the trigger flow.
 */
export function StepFormDeleteChannel({ step }: { step: TriggerStepCreate }) {
  invariant(step.type === "delete_channel");

  const [errorSelect, setErrorSelect] = useState(
    ["Select channel", "$channelId"].includes(step.configs.channelId)
  );

  const defaultValues = {
    name: step.name,
    channelId: step.configs.channelId,
    delayMinutes:
      typeof step.configs.delaySeconds === "number"
        ? step.configs.delaySeconds / 60
        : 5,
  };

  const form = useForm({
    defaultValues,
    mode: "onChange",
  });
  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
  } = form;
  const { state, dispatch, onPersist } = useTriggerEditorStateDispatch();
  const readOnly = !state.trigger.current.isDraft;

  const onSubmit = (data: any) => {
    // This uses the immer library to create a new copy of step (with updates)
    // without affecting the original step.
    const newStep = produce(step, (stepDraft) => {
      stepDraft.name = data.name;
      stepDraft.configs.channelId = data.channelId;
      stepDraft.configs.delaySeconds = data.delayMinutes
        ? data.delayMinutes * 60
        : 0;
    });
    dispatch(updateStep(newStep));
    dispatch(saveDraft(onPersist));
  };

  return (
    <StepFormWithCommonParts
      onSubmit={handleSubmit(onSubmit)}
      form={form}
      step={step}
    >
      <Controller
        control={control}
        name="channelId"
        rules={{
          required: true,
          validate: (channelId) => {
            return (
              channelId !== undefined &&
              channelId !== "Select channel" &&
              channelId !== "$channelId"
            );
          },
        }}
        render={({ field }) => (
          <TriggerEditorChannelSelector
            readOnly={readOnly}
            field={field}
            errorSelect={errorSelect}
            trigger={state.trigger.current}
            onlyCustomChannels={true}
            label="Channel to delete"
            step={step}
          />
        )}
      />
      <FormLabel label="Delete the chanel after">
        <div className="flex w-full	items-end justify-items-center">
          <Input
            type="number"
            style={{ width: "100%" }}
            readOnly={readOnly}
            {...register("delayMinutes", {
              valueAsNumber: true,
              validate: (value) => value >= 0,
            })}
          />
          <p style={{ margin: "0 0 0 15px" }}> Minutes</p>
        </div>
        {errors.delayMinutes && (
          <p className="text-red-500 m-0">Minimum value is 0</p>
        )}
      </FormLabel>
    </StepFormWithCommonParts>
  );
}
